/**
 * Bill detail header — `prototype/purchases/bill-detail.html` ▸ `.composer-head`.
 *
 * Uses page-scoped `.status-pill` + `.match-pill` (NOT `<StatusPill>`
 * from `@flint/ui` — the prototype owns these). Subtitle uses
 * `<p class="sub">` with `.num` / `.sep` / `.due-soft` children. For
 * `approval_pending` bills the primary action is an amber `.split`
 * button cluster for "Approve · OTP ▾".
 */
import { Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Download,
  IndianRupee,
  Pencil,
  Printer,
  RotateCcw,
  ShieldCheck,
  Trash2,
  XCircle,
} from "lucide-react";
import {
  Button,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from "@flint/ui";
import { formatDate, paiseToInrShort } from "../../../lib/format.ts";
import type { BillStatus } from "../list/index.ts";

/** Inputs and action callbacks for the sticky bill detail header. */
export interface BillDetailHeaderProps {
  bill: {
    id: string;
    vendorName: string;
    vendorDocNumber: string | null;
    status: BillStatus;
    receivedOn: string;
    dueOn: string | null;
    totalPaise: string;
    threeWayMatch: "matched" | "mismatch" | "na";
  };
  isFounder: boolean;
  onEdit: () => void;
  onPrint: () => void;
  onDownloadPdf: () => void;
  onTransition: (status: BillStatus) => void;
  /**
   * Launch the record-payment composer. Renders a primary CTA in the header
   * when the bill is in a payable status (`ready_to_pay` / `approval_pending`).
   */
  onRecordPayment: () => void;
  /**
   * Delete the current draft bill (status === 'draft' only). The host
   * opens a confirm dialog before firing `bills.deleteDraft`. Voiding
   * is the wrong verb for drafts — they never happened — so the draft
   * destructive action is delete, not void.
   */
  onDeleteDraft: () => void;
  pending?: boolean;
  /** ADR-0030 permission gates — server enforces; these soften the UX. */
  canCreate?: boolean;
  canApprove?: boolean;
}

/** Render the document-detail composer-head band for bills. */
export function BillDetailHeader({
  bill,
  isFounder,
  onEdit,
  onPrint,
  onDownloadPdf,
  onTransition,
  onRecordPayment,
  onDeleteDraft,
  pending,
  canCreate = true,
  canApprove = isFounder,
}: BillDetailHeaderProps) {
  const dueClass = dueEmphasisClass(bill.status, bill.dueOn);
  return (
    <header className="composer-head">
      <Link to="/procurement/bills" className="composer-head-back" aria-label="Back to bills">
        <ArrowLeft size={16} strokeWidth={2.2} />
      </Link>
      <div className="composer-head-text">
        <h1>
          <span>Bill</span>
          <span className="doc-num">{bill.vendorDocNumber ?? "Draft bill"}</span>
          <span className={`status-pill ${statusVariant(bill.status)}`}>
            {statusLabel(bill.status)}
          </span>
          {bill.threeWayMatch !== "na" ? (
            <span className={`match-pill${bill.threeWayMatch === "mismatch" ? " warn" : ""}`}>
              <Check size={11} strokeWidth={2.4} />
              {bill.threeWayMatch === "matched" ? "3-way match" : "Mismatch"}
            </span>
          ) : null}
        </h1>
        <p className="sub">
          <strong>{bill.vendorName}</strong>
          <span className="sep" aria-hidden>
            ·
          </span>
          <span>
            Received <span className="num">{formatDate(bill.receivedOn)}</span>
          </span>
          {bill.dueOn ? (
            <>
              <span className="sep" aria-hidden>
                ·
              </span>
              <span className={dueClass}>
                Due <span className="num">{formatDate(bill.dueOn)}</span>
              </span>
            </>
          ) : null}
          <span className="sep" aria-hidden>
            ·
          </span>
          <span className="num">{paiseToInrShort(bill.totalPaise)}</span>
        </p>
      </div>
      <div className="composer-head-actions">
        <button
          className="icon-action"
          type="button"
          onClick={onDownloadPdf}
          aria-label="Download bill"
        >
          <Download size={15} />
        </button>
        <button className="icon-action" type="button" onClick={onPrint} aria-label="Print bill">
          <Printer size={15} />
        </button>

        {bill.status === "draft" ? (
          <>
            <Button type="button" intent="ghost" size="sm" onClick={onEdit}>
              <Pencil size={14} />
              Edit
            </Button>
            {canCreate && (
              <Button
                type="button"
                size="sm"
                onClick={() => onTransition("approval_pending")}
                disabled={pending}
              >
                <ShieldCheck size={14} />
                Submit for approval
              </Button>
            )}
            <Button
              type="button"
              intent="ghost"
              size="sm"
              onClick={onDeleteDraft}
              disabled={pending}
              className="text-bad hover:bg-bad-50"
              title="Delete this draft — drafts never happened, so they're removed rather than voided"
            >
              <Trash2 size={14} />
              Delete draft
            </Button>
          </>
        ) : null}

        {bill.status === "review" ? (
          <>
            <Button type="button" intent="ghost" size="sm" onClick={onEdit}>
              <Pencil size={14} />
              Edit
            </Button>
            {canCreate && (
              <Button
                type="button"
                size="sm"
                onClick={() => onTransition("approval_pending")}
                disabled={pending}
              >
                <ShieldCheck size={14} />
                Submit for approval
              </Button>
            )}
          </>
        ) : null}

        {bill.status === "approval_pending" ? (
          <>
            {canApprove && (
              <Button
                type="button"
                intent="ghost"
                size="sm"
                onClick={() => onTransition("review")}
                disabled={pending}
              >
                <RotateCcw size={14} />
                Send back
              </Button>
            )}
            {canApprove && (
              <Button
                type="button"
                intent="ghost"
                size="sm"
                onClick={() => onTransition("void")}
                disabled={pending}
              >
                <XCircle size={14} />
                Reject
              </Button>
            )}
            <Button
              type="button"
              intent="ghost"
              size="sm"
              onClick={onRecordPayment}
              disabled={pending}
              title="Record a payment if you paid this bill outside Flint before approval"
            >
              <IndianRupee size={14} />
              Record payment
            </Button>
            {canApprove ? (
              <div className="split amber">
                <button
                  className="lead"
                  type="button"
                  onClick={() => onTransition("ready_to_pay")}
                  disabled={pending}
                >
                  <ShieldCheck size={13} />
                  Approve · OTP
                </button>
                <DropdownMenuRoot>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="chev"
                      type="button"
                      aria-label="Other approval actions"
                      disabled={pending}
                    >
                      <ChevronDown size={12} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => onTransition("ready_to_pay")}>
                      <ShieldCheck size={14} className="mr-2" /> Approve via OTP
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => onTransition("review")}>
                      <RotateCcw size={14} className="mr-2" /> Send back to review
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenuRoot>
              </div>
            ) : (
              <Button type="button" intent="ghost" size="sm" disabled title="Approval permission required">
                <ShieldCheck size={14} />
                Awaiting approval
              </Button>
            )}
          </>
        ) : null}

        {bill.status === "ready_to_pay" ? (
          <>
            {canApprove && (
              <Button
                type="button"
                intent="ghost"
                size="sm"
                onClick={() => onTransition("void")}
                disabled={pending}
              >
                <XCircle size={14} />
                Void
              </Button>
            )}
            <Button type="button" size="sm" onClick={onRecordPayment} disabled={pending}>
              <IndianRupee size={14} />
              Record payment
            </Button>
          </>
        ) : null}
      </div>
    </header>
  );
}

/** Map bill status to the `.status-pill.{variant}` CSS class. */
export function statusVariant(status: BillStatus): string {
  if (status === "approval_pending") return "approval";
  if (status === "ready_to_pay") return "ready";
  return status;
}

/** Human label for bill statuses in compact detail surfaces. */
export function statusLabel(status: BillStatus): string {
  if (status === "draft") return "Draft";
  if (status === "review") return "In review";
  if (status === "approval_pending") return "Awaiting · founder OTP";
  if (status === "ready_to_pay") return "Ready to pay";
  if (status === "paid") return "Paid";
  return "Voided";
}

function dueEmphasisClass(status: BillStatus, dueOn: string | null): string {
  if (status === "paid" || !dueOn) return "due-ok";
  const days = Math.ceil((new Date(dueOn).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return "due-late";
  if (days <= 7) return "due-soft";
  return "due-ok";
}
